import { HttpClient, HttpErrorResponse, HttpParams, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { catchError, Observable, throwError, of } from 'rxjs';
import { AuthService } from '../../auth-service';
import { ErrorVTO } from '../../../pages/admin-dashboard/products/product.model';
import {
  CurrentStockResultSet,
  StockFilters,
  HistoryStockFilters,
  StockTypeResultSet,
} from '../../../pages/admin-dashboard/stock-management/stock.model';
import { StockIncomingRequest } from '../../../pages/admin-dashboard/stock-management/add-all-stock/add-stock.model';
import { Router } from '@angular/router';
import { CurrentUserService } from '../../current-user.service';
import { OneStockData } from '../../../pages/admin-dashboard/stock-management/edit-stock/edit-stock.model';

@Injectable({
  providedIn: 'root',
})
export class StockService {
  private authService = inject(AuthService);
  private http = inject(HttpClient);
  private API = environment.productsApiUrl;
  private EVENT_MGT_API = environment.eventMgtUrl;
  private router = inject(Router);
  private currentUserService = inject(CurrentUserService);

  private getHeaders(): HttpHeaders {
    const token = this.authService.getAccessToken();
    let headers = new HttpHeaders();
    if (token) headers = headers.set('Authorization', `Bearer ${token}`);
    return headers;
  }

  getHistoryStock(filters: HistoryStockFilters): Observable<CurrentStockResultSet> {
    let params = new HttpParams()
      .set('orderBy', filters.orderBy)
      .set('orderDir', filters.orderDir)
      .set('pageNum', filters.pageNum.toString())
      .set('pageSize', filters.pageSize.toString());

    if (filters.productId) params = params.set('productId', filters.productId.toString());
    if (filters.supplierId) params = params.set('supplierId', filters.supplierId.toString());
    if (filters.typeId) params = params.set('typeId', filters.typeId.toString());
    if (filters.stockType) params = params.set('stockType', filters.stockType);

    return this.http
      .get<CurrentStockResultSet>(`${this.API}/stocks/history`, {
        params,
        headers: this.getHeaders(),
      })
      .pipe(catchError(() => of({ total: 0, list: [] } as CurrentStockResultSet)));
  }

  getStockTypes(): Observable<StockTypeResultSet> {
    return this.http.get<StockTypeResultSet>(`${this.API}/lookup/stock/types`, {
      headers: this.getHeaders(),
    });
  }

  getCurrentStock(filters: StockFilters): Observable<CurrentStockResultSet> {
    let params = new HttpParams()
      .set('pageNum', filters.pageNum.toString())
      .set('pageSize', filters.pageSize.toString())
      .set('orderBy', filters.orderBy)
      .set('orderDir', filters.orderDir);

    if (filters.productId) params = params.set('productId', filters.productId.toString());
    if (filters.supplierId) params = params.set('supplierId', filters.supplierId.toString());
    if (filters.expDateFrom) params = params.set('expDateFrom', filters.expDateFrom);
    if (filters.expDateTo) params = params.set('expDateTo', filters.expDateTo);
    if (filters.isActive !== undefined) params = params.set('isActive', filters.isActive.toString());
    if (filters.stockType) params = params.set('stockType', filters.stockType);

    return this.http
      .get<CurrentStockResultSet>(`${this.API}/stocks/current`, {
        params,
        headers: this.getHeaders(),
      })
      .pipe(catchError(() => of({ total: 0, list: [] } as CurrentStockResultSet)));
  }

  getStockById(currentStockId: string) {
    return this.http
      .get<OneStockData>(`${this.API}/stocks/current/${currentStockId}`, {
        headers: this.getHeaders(),
      })
      .pipe(catchError(this.handleError));
  }

  addIncomingStockToProduct(productId: number, dto: unknown): Observable<unknown> {
    return this.http
      .post(`${this.API}/products/${productId}/stocks/incoming`, dto, {
        headers: this.getHeaders(),
      })
      .pipe(catchError(this.handleError));
  }

  addBulkIncomingStock(data: StockIncomingRequest): Observable<void> {
    return this.http.post<void>(`${this.API}/stocks/incoming`, data, {
      headers: this.getHeaders(),
    });
  }

  activateStock(stockId: string): Observable<void> {
    return this.http.put<void>(
      `${this.EVENT_MGT_API}/stocks/current/${stockId}/activate`,
      {},
      {
        headers: this.getHeaders(),
      },
    );
  }

  deactivateStock(stockId: string): Observable<void> {
    return this.http
      .put<void>(`${this.EVENT_MGT_API}/stocks/current/${stockId}/deactivate`, null, {
        headers: this.getHeaders(),
      })
      .pipe(catchError(this.handleError));
  }

  // private handleError(error: HttpErrorResponse): Observable<never> {
  //   const apiError: ErrorVTO = error.error;
  //   return throwError(() => apiError);
  // }

  private handleError = (error: HttpErrorResponse) => {
    if (error.status === 403) {
      this.router.navigate(['/login']);
      this.currentUserService.logout();
    }
    const apiError: ErrorVTO = error.error;
    return throwError(() => apiError);
  };
}
